import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance, API } from '../App';
import { toast } from 'sonner';
import { Download, Filter, X, FileText, Briefcase, CreditCard, Building2, Eye, ExternalLink } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { convertArabic } from 'arabic-reshaper';
import { NotoSansArabicBase64 } from '../fonts/NotoSansArabic';

const processArabicText = (text) => {
  if (!text) return '';
  const arabicRegex = /[\u0600-\u06FF]/;
  if (!arabicRegex.test(text)) return text;
  return convertArabic(text);
};

// Detail Modal Component
const DetailModal = ({ item, type, onClose }) => {
  if (!item) return null;
  
  const renderContent = () => {
    switch(type) {
      case 'contract':
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><span className="text-gray-500">Contract #:</span> <span className="font-bold text-purple-600">{item.contract_number}</span></div>
              <div><span className="text-gray-500">Status:</span> <span className={`px-2 py-1 rounded text-xs ${item.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100'}`}>{item.status}</span></div>
              <div><span className="text-gray-500">Start:</span> <span className="font-medium">{item.start_date}</span></div>
              <div><span className="text-gray-500">End:</span> <span className="font-medium">{item.end_date}</span></div>
              <div><span className="text-gray-500">Payment Terms:</span> <span className="font-medium">{item.payment_terms}</span></div>
              <div><span className="text-gray-500">Value:</span> <span className="font-bold text-purple-600">{(item.contract_value || 0).toLocaleString()} SAR</span></div>
            </div>
            <div><span className="text-gray-500">Title:</span> <p className="font-medium">{item.contract_title}</p></div>
            {item.description && <div><span className="text-gray-500">Description:</span> <p className="text-sm bg-gray-50 p-2 rounded mt-1">{item.description}</p></div>}
            {/* Attachment Section */}
            {item.attachment_url && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
                <span className="text-gray-500 text-sm font-medium">Attachment:</span>
                <div className="mt-2">
                  {item.attachment_url.toLowerCase().endsWith('.pdf') ? (
                    <div className="flex items-center gap-3 bg-red-50 p-3 rounded-lg">
                      <FileText className="w-10 h-10 text-red-600" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">PDF Document</p>
                        <p className="text-xs text-gray-500">Contract attachment</p>
                      </div>
                      <a
                        href={`${API.replace('/api', '')}${item.attachment_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-red-600 text-white px-3 py-2 rounded-lg text-sm flex items-center gap-1 hover:bg-red-700"
                      >
                        <Eye className="w-4 h-4" /> View PDF
                      </a>
                    </div>
                  ) : (
                    <div className="relative">
                      <img 
                        src={`${API.replace('/api', '')}${item.attachment_url}`}
                        alt="Contract attachment"
                        className="w-full h-40 object-cover rounded-lg"
                      />
                      <a
                        href={`${API.replace('/api', '')}${item.attachment_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded text-xs flex items-center gap-1 hover:bg-opacity-80"
                      >
                        <Eye className="w-3 h-3" /> View Full
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      case 'project':
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><span className="text-gray-500">Project:</span> <span className="font-bold text-blue-600">{item.name}</span></div>
              <div><span className="text-gray-500">Status:</span> <span className={`px-2 py-1 rounded text-xs ${item.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100'}`}>{item.status}</span></div>
              <div><span className="text-gray-500">Client:</span> <span className="font-medium">{item.client_name}</span></div>
              <div><span className="text-gray-500">Contract:</span> <span className="font-medium">{item.contract_number || '-'}</span></div>
              <div><span className="text-gray-500">Start:</span> <span className="font-medium">{item.start_date}</span></div>
              <div><span className="text-gray-500">End:</span> <span className="font-medium">{item.end_date || '-'}</span></div>
              <div><span className="text-gray-500">Budget:</span> <span className="font-bold text-blue-600">{(item.budget || 0).toLocaleString()} SAR</span></div>
              <div><span className="text-gray-500">Spent:</span> <span className="font-bold text-red-600">{(item.spent || 0).toLocaleString()} SAR</span></div>
            </div>
            {item.description && <div><span className="text-gray-500">Description:</span> <p className="text-sm bg-gray-50 p-2 rounded mt-1">{item.description}</p></div>}
          </div>
        );
      case 'quotation':
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><span className="text-gray-500">Quotation #:</span> <span className="font-bold text-yellow-600">{item.quotation_no || item.quotation_number}</span></div>
              <div><span className="text-gray-500">Status:</span> <span className={`px-2 py-1 rounded text-xs ${item.status === 'accepted' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{item.status}</span></div>
              <div><span className="text-gray-500">Date:</span> <span className="font-medium">{item.quotation_date || (item.created_at ? new Date(item.created_at).toLocaleDateString() : '-')}</span></div>
              <div><span className="text-gray-500">Valid Until:</span> <span className="font-medium">{item.valid_date || item.valid_until || '-'}</span></div>
            </div>
            {item.items && item.items.length > 0 && (
              <div>
                <span className="text-gray-500">Items:</span>
                <table className="w-full mt-2 text-sm">
                  <thead className="bg-yellow-50"><tr><th className="p-2 text-left">Description</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Price</th><th className="p-2 text-right">Total</th></tr></thead>
                  <tbody>{item.items.map((it, i) => (<tr key={i} className="border-b"><td className="p-2">{it.description}</td><td className="p-2 text-right">{it.quantity}</td><td className="p-2 text-right">{(it.unit_price || 0).toLocaleString()}</td><td className="p-2 text-right font-medium">{(it.total || 0).toLocaleString()}</td></tr>))}</tbody>
                </table>
              </div>
            )}
            <div className="bg-yellow-50 p-3 rounded grid grid-cols-3 gap-2 text-right">
              <div><span className="text-gray-500">Subtotal:</span> <p className="font-medium">{(item.subtotal || 0).toLocaleString()}</p></div>
              <div><span className="text-gray-500">VAT:</span> <p className="font-medium">{(item.vat_amount || 0).toLocaleString()}</p></div>
              <div><span className="text-gray-500">Total:</span> <p className="font-bold text-yellow-700">{(item.total || 0).toLocaleString()} SAR</p></div>
            </div>
          </div>
        );
      case 'invoice':
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><span className="text-gray-500">Invoice #:</span> <span className="font-bold text-red-600">{item.invoice_number || item.invoice_no}</span></div>
              <div><span className="text-gray-500">Status:</span> <span className={`px-2 py-1 rounded text-xs ${item.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{item.status}</span></div>
              <div><span className="text-gray-500">Date:</span> <span className="font-medium">{new Date(item.created_at).toLocaleDateString()}</span></div>
              <div><span className="text-gray-500">Due Date:</span> <span className="font-medium">{item.due_date || '-'}</span></div>
            </div>
            {item.items && item.items.length > 0 && (
              <div>
                <span className="text-gray-500">Items:</span>
                <table className="w-full mt-2 text-sm">
                  <thead className="bg-red-50"><tr><th className="p-2 text-left">Description</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Price</th><th className="p-2 text-right">Total</th></tr></thead>
                  <tbody>{item.items.map((it, i) => (<tr key={i} className="border-b"><td className="p-2">{it.description}</td><td className="p-2 text-right">{it.quantity}</td><td className="p-2 text-right">{(it.unit_price || 0).toLocaleString()}</td><td className="p-2 text-right font-medium">{(it.total || 0).toLocaleString()}</td></tr>))}</tbody>
                </table>
              </div>
            )}
            <div className="bg-red-50 p-3 rounded grid grid-cols-3 gap-2 text-right">
              <div><span className="text-gray-500">Subtotal:</span> <p className="font-medium">{(item.subtotal || 0).toLocaleString()}</p></div>
              <div><span className="text-gray-500">VAT:</span> <p className="font-medium">{(item.vat_amount || 0).toLocaleString()}</p></div>
              <div><span className="text-gray-500">Total:</span> <p className="font-bold text-red-700">{(item.total || 0).toLocaleString()} SAR</p></div>
            </div>
          </div>
        );
      case 'payment':
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><span className="text-gray-500">Payment #:</span> <span className="font-bold text-green-600">{item.payment_no}</span></div>
              <div><span className="text-gray-500">Type:</span> <span className="font-medium">{item.payment_type}</span></div>
              <div><span className="text-gray-500">Date:</span> <span className="font-medium">{item.payment_date}</span></div>
              <div><span className="text-gray-500">Method:</span> <span className="font-medium">{item.payment_method}</span></div>
              <div><span className="text-gray-500">Amount:</span> <span className="font-bold text-green-600 text-lg">{(item.amount || 0).toLocaleString()} SAR</span></div>
              {item.reference_number && <div><span className="text-gray-500">Reference:</span> <span className="font-medium">{item.reference_number}</span></div>}
            </div>
            {item.description && <div><span className="text-gray-500">Description:</span> <p className="text-sm bg-gray-50 p-2 rounded mt-1">{item.description}</p></div>}
            {/* Attachment Section */}
            {item.attachment_url && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
                <span className="text-gray-500 text-sm font-medium">Attachment:</span>
                <div className="mt-2">
                  {item.attachment_url.toLowerCase().endsWith('.pdf') ? (
                    <div className="flex items-center gap-3 bg-red-50 p-3 rounded-lg">
                      <FileText className="w-10 h-10 text-red-600" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">PDF Document</p>
                        <p className="text-xs text-gray-500">Payment attachment</p>
                      </div>
                      <a
                        href={`${API.replace('/api', '')}${item.attachment_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-red-600 text-white px-3 py-2 rounded-lg text-sm flex items-center gap-1 hover:bg-red-700"
                      >
                        <Eye className="w-4 h-4" /> View PDF
                      </a>
                    </div>
                  ) : (
                    <div className="relative">
                      <img 
                        src={`${API.replace('/api', '')}${item.attachment_url}`}
                        alt="Payment attachment"
                        className="w-full h-40 object-cover rounded-lg"
                      />
                      <a
                        href={`${API.replace('/api', '')}${item.attachment_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded text-xs flex items-center gap-1 hover:bg-opacity-80"
                      >
                        <Eye className="w-3 h-3" /> View Full
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      default:
        return <p>No details available</p>;
    }
  };

  const titles = { contract: 'Contract Details', project: 'Project Details', quotation: 'Quotation Details', invoice: 'Invoice Details', payment: 'Payment Details' };
  const colors = { contract: 'purple', project: 'blue', quotation: 'yellow', invoice: 'red', payment: 'green' };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className={`bg-${colors[type]}-600 text-white p-4 rounded-t-xl flex justify-between items-center`}>
          <h3 className="text-lg font-bold">{titles[type]}</h3>
          <button onClick={onClose} className="hover:bg-white/20 p-1 rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">{renderContent()}</div>
      </div>
    </div>
  );
};

const CustomerReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [customerStats, setCustomerStats] = useState({});
  const [summaryByCustomer, setSummaryByCustomer] = useState({});  // id → {aging, last_activity, opening_balance, ...}
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilter, setShowFilter] = useState(false);

  const openDetail = (item, type) => { setSelectedItem(item); setSelectedItemType(type); };
  const closeDetail = () => { setSelectedItem(null); setSelectedItemType(null); };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const customersRes = await axiosInstance.get('/customers');
      const customersList = customersRes.data;
      setCustomers(customersList);

      // ─── Subledger summary (new engine — single call for ALL customers)
      try {
        const params = new URLSearchParams();
        if (dateFrom) params.append('date_from', dateFrom);
        if (dateTo) params.append('date_to', dateTo);
        const qs = params.toString();
        const sumUrl = `/accounting/customer-statement-summary${qs ? '?' + qs : ''}`;
        const sumRes = await axiosInstance.get(sumUrl);
        const map = {};
        (sumRes.data?.rows || []).forEach((r) => { map[r.id] = r; });
        setSummaryByCustomer(map);
      } catch (e) {
        // Non-fatal — fall back to history-based stats below.
        setSummaryByCustomer({});
      }

      // Fetch stats for each customer (legacy history endpoint — kept for contract/project counts)
      const statsPromises = customersList.map(async (customer) => {
        try {
          let url = `/customers/${customer.id}/history`;
          const params = new URLSearchParams();
          if (dateFrom) params.append('date_from', dateFrom);
          if (dateTo) params.append('date_to', dateTo);
          if (params.toString()) url += `?${params.toString()}`;

          const res = await axiosInstance.get(url);
          return { id: customer.id, stats: res.data.stats, data: res.data };
        } catch (err) {
          return { id: customer.id, stats: {}, data: {} };
        }
      });

      const allStats = await Promise.all(statsPromises);
      const statsMap = {};
      allStats.forEach(s => { statsMap[s.id] = s; });
      setCustomerStats(statsMap);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilter = () => {
    fetchData();
    setShowFilter(false);
  };

  const handleClearFilter = () => {
    setDateFrom('');
    setDateTo('');
    fetchData();
    setShowFilter(false);
  };

  // Calculate totals
  const totals = {
    contracts: 0,
    contractValue: 0,
    projects: 0,
    invoiced: 0,
    paid: 0,
    balance: 0
  };

  customers.forEach(c => {
    const s = customerStats[c.id]?.stats || {};
    totals.contracts += s.total_contracts || 0;
    totals.contractValue += s.total_contract_value || 0;
    totals.projects += s.total_projects || 0;
    totals.invoiced += s.total_invoiced || 0;
    totals.paid += s.total_paid || 0;
    totals.balance += s.balance_due || 0;
  });

  const downloadPDF = () => {
    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.getWidth();
    
    doc.addFileToVFS('NotoSansArabic-Regular.ttf', NotoSansArabicBase64);
    doc.addFont('NotoSansArabic-Regular.ttf', 'NotoSansArabic', 'normal');

    // Header
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(107, 33, 168);
    doc.text('Customer Report', pageWidth / 2, 15, { align: 'center' });
    
    doc.setFont('NotoSansArabic', 'normal');
    doc.setFontSize(12);
    doc.text(processArabicText('تقرير العملاء'), pageWidth / 2, 22, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, 28, { align: 'center' });
    if (dateFrom || dateTo) {
      doc.text(`Period: ${dateFrom || 'Start'} to ${dateTo || 'Current'}`, pageWidth / 2, 33, { align: 'center' });
    }

    // Table
    const tableData = customers.map(c => {
      const s = customerStats[c.id]?.stats || {};
      return [
        c.customer_number,
        c.name_english || c.name_arabic,
        s.total_contracts || 0,
        s.total_projects || 0,
        (s.total_invoiced || 0).toLocaleString(),
        (s.total_paid || 0).toLocaleString(),
        (s.balance_due || 0).toLocaleString()
      ];
    });

    // Add totals row
    tableData.push([
      '', 'TOTAL',
      totals.contracts,
      totals.projects,
      totals.invoiced.toLocaleString(),
      totals.paid.toLocaleString(),
      totals.balance.toLocaleString()
    ]);

    autoTable(doc, {
      startY: 40,
      head: [['Customer #', 'Name', 'Contracts', 'Projects', 'Invoiced (SAR)', 'Paid (SAR)', 'Balance (SAR)']],
      body: tableData,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [107, 33, 168] },
      footStyles: { fillColor: [243, 244, 246], textColor: [0, 0, 0], fontStyle: 'bold' }
    });

    doc.save('Customer_Report.pdf');
    toast.success('PDF downloaded');
  };

  if (loading) {
    return <div className="text-center py-12">Loading report...</div>;
  }

  return (
    <div data-testid="customer-report-page">
      <div className="page-header">
        <div>
          <h2 className="page-title">Customer Report</h2>
          <p className="text-gray-500 text-sm">تقرير العملاء</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <button
              onClick={() => setShowFilter(!showFilter)}
              className={`btn-secondary flex items-center gap-2 ${(dateFrom || dateTo) ? 'ring-2 ring-purple-500' : ''}`}
            >
              <Filter className="w-4 h-4" />
              Filter
            </button>
            
            {showFilter && (
              <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50 w-72">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-semibold">Filter by Date</h4>
                  <button onClick={() => setShowFilter(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">From</label>
                    <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">To</label>
                    <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={handleApplyFilter} className="btn-primary flex-1 text-sm">Apply</button>
                    <button onClick={handleClearFilter} className="btn-secondary text-sm">Clear</button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <button onClick={downloadPDF} className="btn-primary flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export PDF
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="w-5 h-5 text-purple-600" />
            <p className="text-purple-600 text-sm font-medium">Customers</p>
          </div>
          <p className="text-2xl font-bold text-purple-700">{customers.length}</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-5 h-5 text-blue-600" />
            <p className="text-blue-600 text-sm font-medium">Contracts</p>
          </div>
          <p className="text-2xl font-bold text-blue-700">{totals.contracts}</p>
        </div>
        <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100">
          <div className="flex items-center gap-2 mb-2">
            <Briefcase className="w-5 h-5 text-indigo-600" />
            <p className="text-indigo-600 text-sm font-medium">Projects</p>
          </div>
          <p className="text-2xl font-bold text-indigo-700">{totals.projects}</p>
        </div>
        <div className="bg-red-50 rounded-xl p-4 border border-red-100">
          <p className="text-red-600 text-sm font-medium mb-2">Total Invoiced</p>
          <p className="text-xl font-bold text-red-700">{totals.invoiced.toLocaleString()}</p>
          <p className="text-xs text-red-500">SAR</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 border border-green-100">
          <p className="text-green-600 text-sm font-medium mb-2">Total Paid</p>
          <p className="text-xl font-bold text-green-700">{totals.paid.toLocaleString()}</p>
          <p className="text-xs text-green-500">SAR</p>
        </div>
        <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
          <p className="text-orange-600 text-sm font-medium mb-2">Total Balance</p>
          <p className="text-xl font-bold text-orange-700">{totals.balance.toLocaleString()}</p>
          <p className="text-xs text-orange-500">SAR Due</p>
        </div>
      </div>

      {/* Date Range Info */}
      {(dateFrom || dateTo) && (
        <div className="bg-purple-50 rounded-lg p-3 mb-4 text-sm text-purple-700">
          <strong>Period:</strong> {dateFrom || 'Start'} to {dateTo || 'Current'}
        </div>
      )}

      {/* Table + Side Panel */}
      <div className="flex gap-4">
        <div className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-300 ${selectedCustomer ? 'w-1/2' : 'w-full'}`}>
          <table className="w-full">
            <thead className="bg-purple-600 text-white">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-semibold">Customer #</th>
                <th className="px-3 py-3 text-left text-xs font-semibold">Name</th>
                <th className="px-3 py-3 text-center text-xs font-semibold">Contracts</th>
                <th className="px-3 py-3 text-center text-xs font-semibold">Projects</th>
                <th className="px-3 py-3 text-right text-xs font-semibold">Invoiced</th>
                <th className="px-3 py-3 text-right text-xs font-semibold">Paid</th>
                <th className="px-3 py-3 text-right text-xs font-semibold">Balance</th>
                <th className="px-3 py-3 text-right text-xs font-semibold" title="120+ days overdue">120+ d</th>
                <th className="px-3 py-3 text-center text-xs font-semibold">Statement</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {customers.map((customer, idx) => {
                const s = customerStats[customer.id]?.stats || {};
                const sum = summaryByCustomer[customer.id] || {};
                const closing = (sum.closing_balance != null) ? sum.closing_balance : (s.balance_due || 0);
                const invoicedAcc = (sum.total_invoiced != null) ? sum.total_invoiced : (s.total_invoiced || 0);
                const paidAcc = (sum.total_paid != null) ? sum.total_paid : (s.total_paid || 0);
                const overdue120 = sum.aging?.d_120_plus || 0;
                const isSelected = selectedCustomer?.id === customer.id;
                return (
                  <tr
                    key={customer.id}
                    onClick={() => setSelectedCustomer(isSelected ? null : { ...customer, ...customerStats[customer.id] })}
                    className={`cursor-pointer hover:bg-purple-50 transition-colors ${isSelected ? 'bg-purple-100 border-l-4 border-purple-500' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                    data-testid={`customer-report-row-${customer.customer_number || customer.id}`}
                  >
                    <td className="px-3 py-3 font-medium text-purple-600">{customer.customer_number}</td>
                    <td className="px-3 py-3">
                      <p className="font-medium text-sm">{customer.name_english}</p>
                    </td>
                    <td className="px-3 py-3 text-center text-sm">{s.total_contracts || 0}</td>
                    <td className="px-3 py-3 text-center text-sm">{s.total_projects || 0}</td>
                    <td className="px-3 py-3 text-right text-sm font-medium text-red-600">{invoicedAcc.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="px-3 py-3 text-right text-sm font-medium text-green-600">{paidAcc.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="px-3 py-3 text-right text-sm font-bold text-orange-600">{closing.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className={`px-3 py-3 text-right text-sm font-bold ${overdue120 > 0 ? 'text-red-700 bg-red-50' : 'text-gray-400'}`}>
                      {overdue120 > 0 ? overdue120.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <button
                        data-testid={`customer-report-open-statement-${customer.customer_number || customer.id}`}
                        onClick={(e) => { e.stopPropagation(); navigate(`/customer-statement/${customer.id}`); }}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-purple-300 text-purple-700 hover:bg-purple-600 hover:text-white transition"
                      >
                        <ExternalLink className="w-3 h-3" /> Open
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-100 font-bold">
              <tr>
                <td className="px-3 py-3 text-sm" colSpan="2">TOTAL</td>
                <td className="px-3 py-3 text-center text-sm">{totals.contracts}</td>
                <td className="px-3 py-3 text-center text-sm">{totals.projects}</td>
                <td className="px-3 py-3 text-right text-sm text-red-600">{totals.invoiced.toLocaleString()}</td>
                <td className="px-3 py-3 text-right text-sm text-green-600">{totals.paid.toLocaleString()}</td>
                <td className="px-3 py-3 text-right text-sm text-orange-600">{totals.balance.toLocaleString()}</td>
                <td className="px-3 py-3 text-right text-sm text-red-700">
                  {Object.values(summaryByCustomer).reduce((acc, r) => acc + (r.aging?.d_120_plus || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                <td className="px-3 py-3 text-center text-xs text-gray-400">—</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Customer Details Side Panel */}
        {selectedCustomer && (
          <div className="w-1/2 bg-white rounded-xl shadow-lg border border-gray-200 p-5 sticky top-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-sm text-purple-600 font-semibold">{selectedCustomer.customer_number}</p>
                <h3 className="text-xl font-bold text-gray-900">{selectedCustomer.name_english}</h3>
                {selectedCustomer.name_arabic && <p className="text-gray-600" dir="rtl">{selectedCustomer.name_arabic}</p>}
              </div>
              <button onClick={() => setSelectedCustomer(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Financial Summary */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-3 mb-4">
              <div className="grid grid-cols-4 gap-2 text-center text-sm">
                <div><p className="text-lg font-bold text-purple-600">{(selectedCustomer.stats?.total_contract_value || 0).toLocaleString()}</p><p className="text-xs text-gray-500">Contract Value</p></div>
                <div><p className="text-lg font-bold text-red-600">{(selectedCustomer.stats?.total_invoiced || 0).toLocaleString()}</p><p className="text-xs text-gray-500">Invoiced</p></div>
                <div><p className="text-lg font-bold text-green-600">{(selectedCustomer.stats?.total_paid || 0).toLocaleString()}</p><p className="text-xs text-gray-500">Paid</p></div>
                <div><p className="text-lg font-bold text-orange-600">{(selectedCustomer.stats?.balance_due || 0).toLocaleString()}</p><p className="text-xs text-gray-500">Balance</p></div>
              </div>
            </div>

            {/* All Contracts */}
            <div className="mb-4">
              <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2 bg-purple-100 p-2 rounded">
                <FileText className="w-4 h-4 text-purple-600" /> Contracts ({selectedCustomer.data?.contracts?.length || 0})
              </h4>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {selectedCustomer.data?.contracts?.length > 0 ? selectedCustomer.data.contracts.map((c, i) => (
                  <div key={i} onClick={() => openDetail(c, 'contract')} className="flex justify-between items-center bg-purple-50 rounded p-2 text-sm cursor-pointer hover:bg-purple-100 transition-colors">
                    <div>
                      <p className="font-medium text-purple-700">{c.contract_number}</p>
                      <p className="text-xs text-gray-500">{c.contract_title}</p>
                    </div>
                    <span className="font-bold text-purple-600">{(c.contract_value || 0).toLocaleString()}</span>
                  </div>
                )) : <p className="text-gray-400 text-sm text-center py-2">No contracts</p>}
              </div>
            </div>

            {/* All Projects */}
            <div className="mb-4">
              <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2 bg-blue-100 p-2 rounded">
                <Briefcase className="w-4 h-4 text-blue-600" /> Projects ({selectedCustomer.data?.projects?.length || 0})
              </h4>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {selectedCustomer.data?.projects?.length > 0 ? selectedCustomer.data.projects.map((p, i) => (
                  <div key={i} onClick={() => openDetail(p, 'project')} className="flex justify-between items-center bg-blue-50 rounded p-2 text-sm cursor-pointer hover:bg-blue-100 transition-colors">
                    <div>
                      <p className="font-medium text-blue-700">{p.name}</p>
                      <p className="text-xs text-gray-500">{p.status} - {p.start_date}</p>
                    </div>
                    <span className="font-bold text-blue-600">{(p.budget || 0).toLocaleString()}</span>
                  </div>
                )) : <p className="text-gray-400 text-sm text-center py-2">No projects</p>}
              </div>
            </div>

            {/* All Quotations */}
            <div className="mb-4">
              <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2 bg-yellow-100 p-2 rounded">
                <FileText className="w-4 h-4 text-yellow-600" /> Quotations ({selectedCustomer.data?.quotations?.length || 0})
              </h4>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {selectedCustomer.data?.quotations?.length > 0 ? selectedCustomer.data.quotations.map((q, i) => (
                  <div key={i} onClick={() => openDetail(q, 'quotation')} className="flex justify-between items-center bg-yellow-50 rounded p-2 text-sm cursor-pointer hover:bg-yellow-100 transition-colors">
                    <div>
                      <p className="font-medium text-yellow-700">{q.quotation_no || q.quotation_number}</p>
                      <p className="text-xs text-gray-500">{q.status} - {q.quotation_date || new Date(q.created_at).toLocaleDateString()}</p>
                    </div>
                    <span className="font-bold text-yellow-600">{(q.total || 0).toLocaleString()}</span>
                  </div>
                )) : <p className="text-gray-400 text-sm text-center py-2">No quotations</p>}
              </div>
            </div>

            {/* All Invoices */}
            <div className="mb-4">
              <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2 bg-red-100 p-2 rounded">
                <FileText className="w-4 h-4 text-red-600" /> Invoices ({selectedCustomer.data?.invoices?.length || 0})
              </h4>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {selectedCustomer.data?.invoices?.length > 0 ? selectedCustomer.data.invoices.map((inv, i) => (
                  <div key={i} onClick={() => openDetail(inv, 'invoice')} className="flex justify-between items-center bg-red-50 rounded p-2 text-sm cursor-pointer hover:bg-red-100 transition-colors">
                    <div>
                      <p className="font-medium text-red-700">{inv.invoice_number || inv.invoice_no}</p>
                      <p className="text-xs text-gray-500">{inv.status} - {new Date(inv.created_at).toLocaleDateString()}</p>
                    </div>
                    <span className="font-bold text-red-600">{(inv.total || 0).toLocaleString()}</span>
                  </div>
                )) : <p className="text-gray-400 text-sm text-center py-2">No invoices</p>}
              </div>
            </div>

            {/* All Payments */}
            <div className="mb-2">
              <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2 bg-green-100 p-2 rounded">
                <CreditCard className="w-4 h-4 text-green-600" /> Payments ({selectedCustomer.data?.payments?.length || 0})
              </h4>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {selectedCustomer.data?.payments?.length > 0 ? selectedCustomer.data.payments.map((p, i) => (
                  <div key={i} onClick={() => openDetail(p, 'payment')} className="flex justify-between items-center bg-green-50 rounded p-2 text-sm cursor-pointer hover:bg-green-100 transition-colors">
                    <div>
                      <p className="font-medium text-green-700">{p.payment_no}</p>
                      <p className="text-xs text-gray-500">{p.payment_method} - {p.payment_date}</p>
                    </div>
                    <span className="font-bold text-green-600">{(p.amount || 0).toLocaleString()}</span>
                  </div>
                )) : <p className="text-gray-400 text-sm text-center py-2">No payments</p>}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedItem && <DetailModal item={selectedItem} type={selectedItemType} onClose={closeDetail} />}
    </div>
  );
};

export default CustomerReport;
